import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  split,
  type DefaultOptions,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient as createWsClient } from "graphql-ws";
import { authJwtLink } from "./links/auth-jwt";
import {
  createNanoIdAuthLink,
  type NanoIdAuthConfig,
} from "./links/auth-nanoid";

/**
 * Apollo client factory shared by all POLF apps.
 *
 * Auth modes:
 *   - "jwt"      → cookie-based, sends credentials. Admin (JWT shell).
 *   - "operator" → operator-key header. Admin (operator shell).
 *   - "player"   → player-key header. Player app.
 *   - "event"    → polf-event-key header. Projector + public leaderboard views.
 *   - "opponent" → opponent-key header. Public Beat-the-Pro audience view.
 *
 * The factory wires the same HTTP + WS pipeline with mode-appropriate auth
 * and error handling. Subscriptions go through graphql-ws at /graphql; HTTP
 * queries/mutations through fetch at /graphql.
 */

export type AuthMode = "jwt" | "operator" | "player" | "event" | "opponent";

export type CreateClientOptions = {
  authMode: AuthMode;
  /** Absolute or relative GraphQL endpoint. Default: "/graphql". */
  uri?: string;
  /** Absolute WS endpoint. Default: derived from uri. */
  wsUri?: string;
  /**
   * For nano-id auth modes — provide the key getter. Ignored for "jwt".
   * Apps typically pass `() => localStorage.getItem("polf-player-key")`.
   */
  getNanoIdKey?: NanoIdAuthConfig["getKey"];
  /**
   * Called on 401/403 responses. Apps wire this to a logout/redirect flow.
   */
  onUnauthorized?: () => void;
  /**
   * Called for NON-auth errors (network / GraphQL). Apps may wire this to a
   * toast. Auth errors (401/403) go through `onUnauthorized` instead. Non-auth
   * errors are always `console.error`'d regardless of whether this is set, so
   * a live failure is never silently swallowed.
   */
  onError?: (message: string) => void;
  /**
   * Optional cache type policies (merge functions). Apps may override per-
   * field defaults declared here.
   */
  cacheTypePolicies?: InMemoryCache["policies"];
  /**
   * Opt-in per-operation timeout (ms). When set, every non-subscription
   * operation is raced against an AbortController that fires after this many
   * ms, so a request hung on a flaky connection rejects with a network error
   * instead of leaving a mutation's `loading` state stuck forever (which
   * freezes the UI that disables on it). Default: no timeout — the browser's
   * own timeout applies. Only the player app sets this today; leaving it unset
   * keeps admin/projector/operator behaviour unchanged.
   */
  operationTimeoutMs?: number;
  /**
   * REQUIRED for any query using union or interface fragments. Without it,
   * InMemoryCache can't tell which inline-fragment matches a payload, so
   * fields under `... on X` get silently dropped on read. Map each abstract
   * type to its concrete variants.
   *
   * Generate via the codegen `@graphql-codegen/fragment-matcher` plugin, or
   * hardcode for small surfaces.
   */
  possibleTypes?: Record<string, string[]>;
};

const HEADER_BY_MODE: Record<Exclude<AuthMode, "jwt">, string> = {
  operator: "operator-key",
  player: "player-key",
  event: "polf-event-key",
  opponent: "opponent-key",
};

const deriveWsUri = (httpUri: string): string => {
  if (httpUri.startsWith("http")) return httpUri.replace(/^http/, "ws");
  if (typeof window === "undefined") return httpUri;
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${httpUri}`;
};

export const createApolloClient = (
  opts: CreateClientOptions,
): ApolloClient<unknown> => {
  const uri = opts.uri ?? "/graphql";
  const wsUri = opts.wsUri ?? deriveWsUri(uri);

  const httpLink = new HttpLink({
    uri,
    credentials: opts.authMode === "jwt" ? "include" : "same-origin",
  });

  const wsLink = new GraphQLWsLink(
    createWsClient({
      url: wsUri,
      connectionParams: () => {
        if (opts.authMode === "jwt") return {};
        const key = opts.getNanoIdKey?.();
        if (!key) return {};
        return { [HEADER_BY_MODE[opts.authMode]]: key };
      },
    }),
  );

  const transportLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return (
        def.kind === "OperationDefinition" && def.operation === "subscription"
      );
    },
    wsLink,
    httpLink,
  );

  // Opt-in timeout: abort a hung HTTP operation after `operationTimeoutMs` so
  // its promise rejects (clearing any `loading` flag the UI disables on) rather
  // than hanging on a dead/flaky connection. Subscriptions are exempt (they're
  // long-lived by design and go over WS, not fetch).
  const timeoutLink = new ApolloLink((operation, forward) => {
    const def = getMainDefinition(operation.query);
    const isSubscription =
      def.kind === "OperationDefinition" && def.operation === "subscription";
    if (!opts.operationTimeoutMs || isSubscription) return forward(operation);
    const controller = new AbortController();
    operation.setContext((prev: { fetchOptions?: RequestInit }) => ({
      ...prev,
      fetchOptions: { ...(prev.fetchOptions ?? {}), signal: controller.signal },
    }));
    return new Observable((observer) => {
      const timer = setTimeout(
        () => controller.abort(),
        opts.operationTimeoutMs,
      );
      const sub = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          clearTimeout(timer);
          observer.error(err);
        },
        complete: () => {
          clearTimeout(timer);
          observer.complete();
        },
      });
      return () => {
        clearTimeout(timer);
        sub.unsubscribe();
      };
    });
  });

  const authLink =
    opts.authMode === "jwt"
      ? authJwtLink
      : createNanoIdAuthLink({
          header: HEADER_BY_MODE[opts.authMode],
          getKey: opts.getNanoIdKey ?? (() => null),
        });

  const errorLink = onError(({ operation, networkError, graphQLErrors }) => {
    const status = (networkError as { statusCode?: number } | null)?.statusCode;
    const isAuthErr =
      status === 401 ||
      status === 403 ||
      graphQLErrors?.some((e) => {
        const code = (e.extensions?.code as string | undefined) ?? "";
        return code === "UNAUTHENTICATED" || code === "FORBIDDEN";
      });
    if (isAuthErr) {
      opts.onUnauthorized?.();
      return;
    }
    // Non-auth errors were previously swallowed silently. Surface them so a live
    // failure is visible in logs (and optionally to the app via onError).
    if (networkError || (graphQLErrors && graphQLErrors.length > 0)) {
      console.error("[apollo]", operation.operationName, {
        networkError,
        graphQLErrors,
      });
      const message =
        graphQLErrors?.[0]?.message ??
        (networkError as Error | null)?.message ??
        "Network error";
      opts.onError?.(message);
    }
  });

  const defaultOptions: DefaultOptions = {
    watchQuery: { fetchPolicy: "cache-and-network", errorPolicy: "all" },
    query: { fetchPolicy: "network-only", errorPolicy: "all" },
  };

  return new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, timeoutLink, transportLink]),
    cache: new InMemoryCache({
      ...(opts.cacheTypePolicies
        ? { typePolicies: opts.cacheTypePolicies as never }
        : {}),
      ...(opts.possibleTypes ? { possibleTypes: opts.possibleTypes } : {}),
    }),
    defaultOptions,
    connectToDevTools:
      typeof window !== "undefined" && window.location.hostname === "localhost",
  });
};
